/**
 * Recognises crashes caused by an open tab running an older build than the
 * deployed backend or Next.js app ("version skew"). These are fixed by a
 * reload, so error boundaries show an update screen instead of the error page.
 */

/** A crash this soon after an observed skew error is treated as skew too. */
export const DEPLOY_SKEW_SIGNAL_WINDOW_MS = 30_000;

const SKEW_MESSAGE_PATTERNS: readonly RegExp[] = [
  // Convex: a paginated query's filter changed, so old cursors are rejected.
  /InvalidCursor/,
  // Convex: the function was renamed or removed by the deploy.
  /Could not find public function for/,
  // Next.js / bundler chunks that no longer exist on the new deployment.
  /ChunkLoadError/,
  /Loading (?:CSS )?chunk [\w-]+ failed/,
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
  // Next.js: the Server Action id belongs to the previous build.
  /Failed to find Server Action/,
];

const REACT_TOO_MANY_RENDERS_PATTERN =
  /Minified React error #301|Too many re-renders/;

let lastSignalAt: number | null = null;

function readMessage(error: unknown): string | null {
  if (typeof error === 'string') return error;
  if (typeof error !== 'object' || error === null) return null;
  if (!('message' in error)) return null;
  return typeof error.message === 'string' ? error.message : null;
}

function readName(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('name' in error)) {
    return null;
  }
  return typeof error.name === 'string' ? error.name : null;
}

function hasInvalidCursorData(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return false;
  }
  const data = error.data;
  return (
    typeof data === 'object' &&
    data !== null &&
    'paginationError' in data &&
    data.paginationError === 'InvalidCursor'
  );
}

/** The error itself says the tab is out of date with the deployment. */
export function isDeploySkewError(error: unknown): boolean {
  if (readName(error) === 'ChunkLoadError') return true;
  if (hasInvalidCursorData(error)) return true;
  const message = readMessage(error);
  return (
    message !== null &&
    SKEW_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  );
}

/** React gave up re-rendering, often the secondary effect of a skew reset. */
export function isTooManyRendersError(error: unknown): boolean {
  const message = readMessage(error);
  return message !== null && REACT_TOO_MANY_RENDERS_PATTERN.test(message);
}

/** Remember that a skew error was seen; browser only, never sent anywhere. */
export function recordDeploySkewSignal(now: number = Date.now()): void {
  if (typeof window === 'undefined') return;
  lastSignalAt = now;
}

export function hasRecentDeploySkewSignal(now: number = Date.now()): boolean {
  return (
    lastSignalAt !== null &&
    now >= lastSignalAt &&
    now - lastSignalAt <= DEPLOY_SKEW_SIGNAL_WINDOW_MS
  );
}

export function clearDeploySkewSignal(): void {
  lastSignalAt = null;
}

/**
 * Whether a boundary should show the update screen for this crash: a skew
 * error itself, or any crash shortly after one was observed (the boundary may
 * only see the follow-on failure, such as React #301).
 */
export function isDeploySkewCrash(
  error: unknown,
  now: number = Date.now()
): boolean {
  if (isDeploySkewError(error)) {
    recordDeploySkewSignal(now);
    return true;
  }
  return hasRecentDeploySkewSignal(now);
}

/** Inspects log arguments (e.g. Convex client warnings) for skew errors. */
export function recordDeploySkewSignalFromLog(args: readonly unknown[]): void {
  if (args.some(isDeploySkewError)) recordDeploySkewSignal();
}
